import {
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";

import { useMutation, useQuery } from "react-query";
import { useState } from "react";
import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IBaselinesService } from "@shared/interfaces/baselines-service.interface";
import {
  BaselineDto,
  CreateBaselineDto,
  UpdateBaselineDto,
} from "@shared/types/baselines/baselines.dto";
import dayjs from "dayjs";

const Baselines = () => {
  const baselinesService = appInject<IBaselinesService>(
    DI_TOKENS.baselinesService,
  );
  const [viewMode, setViewMode] = useState<"positive" | "negative">("positive");

  // State
  const [localBaselines, setLocalBaselines] = useState<BaselineDto[]>([]);

  // Constants
  const NEUTRAL_GRADE = 0;

  // Fetch initial data
  const { refetch, isLoading } = useQuery(
    ["baselines-global"],
    () => baselinesService.list(),
    {
      onSuccess: (data) => {
        setLocalBaselines(data);
      },
    },
  );

  const sortedBaselines = [...localBaselines].sort(
    (a, b) => a.draftGrade - b.draftGrade,
  );
  const minGrade =
    sortedBaselines.length > 0 ? sortedBaselines[0].draftGrade : NEUTRAL_GRADE;
  const maxGrade =
    sortedBaselines.length > 0
      ? sortedBaselines[sortedBaselines.length - 1].draftGrade
      : NEUTRAL_GRADE;

  const filteredBaselines = sortedBaselines
    .filter((b) =>
      viewMode === "positive"
        ? b.draftGrade >= NEUTRAL_GRADE
        : b.draftGrade < NEUTRAL_GRADE,
    )
    .sort((a, b) =>
      viewMode === "positive"
        ? a.draftGrade - b.draftGrade
        : b.draftGrade - a.draftGrade,
    );

  const saveMutation = useMutation(
    (items: BaselineDto[]) => {
      // Validate
      const invalidGLO = items.some(
        (i) => i.gloChange === null || i.gloChange === undefined,
      );
      if (invalidGLO) {
        return Promise.reject(new Error("All GLO values must be filled"));
      }
      return baselinesService.saveAll(items);
    },
    {
      onSuccess: (data) => {
        message.success("All baselines saved successfully");
        setLocalBaselines(data);
        refetch();
      },
      onError: (err: any) => {
        message.error(err.message || "Failed to save sequence");
      },
    },
  );

  const handleDelete = (id: string) => {
    setLocalBaselines((prev) => prev.filter((b) => b.id !== id));
  };

  const handleLocalChange = (
    id: string,
    field: keyof BaselineDto,
    value: any,
  ) => {
    setLocalBaselines((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)),
    );
  };

  const handleAddNext = () => {
    let newGrade = 0;

    if (viewMode === "positive") {
      // If no positives yet, start at 0 (neutral) or 1? Neutral is 0.
      // If current max < 0, then we are adding first positive? No, filtered view handles it.
      // If localBaselines is empty: 0.
      // If maxGrade < NEUTRAL_GRADE (only negatives exist): 0.
      newGrade = maxGrade < NEUTRAL_GRADE ? NEUTRAL_GRADE : maxGrade + 1;
    } else {
      // If minGrade > NEUTRAL_GRADE (only positives exist): -1.
      // If minGrade == NEUTRAL_GRADE: -1.
      newGrade = minGrade > NEUTRAL_GRADE ? -1 : minGrade - 1;
    }

    // Check if exists
    if (localBaselines.some((b) => b.draftGrade === newGrade)) {
      message.warning(`Baseline for grade ${newGrade} already exists`);
      return;
    }

    const newBaseline: BaselineDto = {
      id: `temp-${Date.now()}`,
      draftGrade: newGrade,
      gloChange: 0,
      percentile: 0,
    };

    setLocalBaselines((prev) => [...prev, newBaseline]);

    // Scroll to bottom after render
    setTimeout(() => {
      const tableBody = document.querySelector(".ant-table-body");
      if (tableBody) {
        tableBody.scrollTo({ top: tableBody.scrollHeight, behavior: "smooth" });
      }
    }, 100);
  };

  const columns = [
    {
      title: "Draft Grade",
      dataIndex: "draftGrade",
      width: 120,
      render: (val: number) => (
        <Space>
          {val === NEUTRAL_GRADE ? "Baseline" : val < 0 ? val : `+${val}`}
        </Space>
      ),
    },
    {
      title: "GLO Change",
      dataIndex: "gloChange",
      width: 150,
      render: (val: number, record: BaselineDto) => (
        <InputNumber
          value={val}
          onChange={(v) => handleLocalChange(record.id, "gloChange", v)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Percentile",
      dataIndex: "percentile",
      width: 150,
      render: (val: number, record: BaselineDto) => (
        <InputNumber
          value={val}
          onChange={(v) => handleLocalChange(record.id, "percentile", v)}
          step={0.1}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 40,
      render: (_: any, record: BaselineDto) => {
        const isEndpoint =
          record.draftGrade === minGrade || record.draftGrade === maxGrade;
        return (
          <Space>
            {isEndpoint && (
              <Button
                type="primary"
                danger
                size="small"
                onClick={() => handleDelete(record.id)}
              >
                Delete
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "20px", height: "100%", overflowY: "auto" }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Col>
          <Typography.Title level={4}>
            Baselines ({localBaselines.length})
          </Typography.Title>
        </Col>
        <Col>
          <Space>
            <Select
              value={viewMode}
              onChange={(val) => setViewMode(val)}
              style={{ width: 220 }}
            >
              <Select.Option value="positive">Positives</Select.Option>
              <Select.Option value="negative">Negatives</Select.Option>
            </Select>
            <Button
              type="primary"
              onClick={() => saveMutation.mutate(localBaselines)}
              loading={saveMutation.isLoading}
            >
              Save Changes
            </Button>
          </Space>
        </Col>
      </Row>

      <Table
        dataSource={filteredBaselines}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        pagination={false}
        size="small"
        scroll={{ y: 600 }}
      />

      <div style={{ marginTop: 16, textAlign: "center" }}>
        <Button
          onClick={handleAddNext}
          type="primary"
          style={{ width: "100%", maxWidth: 200 }}
        >
          Add Next {viewMode === "positive" ? "Positive" : "Negative"}
        </Button>
      </div>
    </div>
  );
};

export default Baselines;
